import React, { useState, useRef, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { Link, useLocation, useNavigate } from 'react-router-dom';
import { Button } from '../components/ui/button';
import { Card, CardContent } from '../components/ui/card';
import { Bed, Bath, Sparkles, Plus, Minus, Zap, Home as HomeIcon, Wind, ChefHat, ShowerHead, Footprints, Package, ArrowLeft, Printer, Phone, Trash2, Check, Download, Mail, X, Building2, Thermometer, Lightbulb, Droplets, PaintBucket, UtensilsCrossed, Waves, DoorOpen, Wifi, Layers, CircleDot, Plug, Lamp, Shield, Pipette, Sofa, Refrigerator, Bath as BathIcon, Fan, Square, KeyRound } from 'lucide-react';
import { toast } from 'sonner';
import axios from 'axios';
import { getUserProfile } from '../utils/userProfile';
import useGooglePlacesAutocomplete from '../hooks/useGooglePlaces';
import ROIAnalysis from '../components/ROIAnalysis';
import InteractiveMap from '../components/InteractiveMap';
import { PrivacyConsent } from '../components/ConsentCheckbox';
import Footer from '../components/sections/Footer';
import { packages as packageData } from '../utils/packageData';
import html2canvas from 'html2canvas';
import jsPDF from 'jspdf';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;
const GOOGLE_MAPS_API_KEY = process.env.REACT_APP_GOOGLE_MAPS_API_KEY;

// Room to recommended size mapping
const getRoomSizeRange = (beds, baths) => {
  if (beds === 2 && baths === 1) return { min: 500, max: 600, mid: 550 };
  if (beds === 2 && baths === 2) return { min: 601, max: 800, mid: 700 };
  if (beds === 3 && baths === 2) return { min: 801, max: 1100, mid: 950 };
  if ((beds >= 3 && baths >= 2) || beds >= 4) return { min: 1101, max: 1200, mid: 1150 };
  if (beds === 1 && baths === 1) return { min: 500, max: 600, mid: 550 };
  if (beds === 1 && baths === 2) return { min: 601, max: 800, mid: 700 };
  return { min: 500, max: 1200, mid: 850 };
};

const QuoteCalculator = () => {
  const { t, i18n } = useTranslation();
  const isZh = i18n.language === 'zh';
  const location = useLocation();
  const navigate = useNavigate();
  const prefill = location.state || {};
  const addressInputRef = useRef(null);
  const printRef = useRef(null);
  const [loading, setLoading] = useState(false);
  const [exportingPdf, setExportingPdf] = useState(false);
  const [sendingEmail, setSendingEmail] = useState(false);
  const [showEmailModal, setShowEmailModal] = useState(false);
  const [emailAddress, setEmailAddress] = useState('');
  const [emailName, setEmailName] = useState('');
  const [emailPrivacyAgreed, setEmailPrivacyAgreed] = useState(false);
  const [generatedQuote, setGeneratedQuote] = useState(null);
  const [showQuoteResult, setShowQuoteResult] = useState(false);
  const [mapUrl, setMapUrl] = useState(null);
  const [customUpgradeName, setCustomUpgradeName] = useState('');
  const [customUpgradePrice, setCustomUpgradePrice] = useState('');
  const [propertyValue, setPropertyValue] = useState('');
  const [addressConfirmed, setAddressConfirmed] = useState(!!prefill.property_address);

  // Login gate: check on mount, redirect if not logged in
  useEffect(() => {
    const token = localStorage.getItem('admin_token');
    if (!token) {
      toast.error(isZh ? '请登录系统' : 'Please login first');
      navigate('/profile', { state: { returnTo: location.pathname, returnState: prefill } });
      return;
    }
    // Prefill from user profile
    const loadProfile = async () => {
      const prof = await getUserProfile();
      if (prof) {
        setFormData(prev => ({
          ...prev,
          customer_name: prev.customer_name || prof.name || '',
          customer_email: prev.customer_email || prof.email || '',
          customer_phone: prev.customer_phone || prof.phone || '',
          property_address: prev.property_address || prof.address || '',
        }));
        if (prof.address) setAddressConfirmed(true);
      }
    };
    loadProfile();
  }, []);

  // Auto-fill email/phone from logged-in user
  const loggedEmail = localStorage.getItem('admin_email') || '';
  const autoEmail = loggedEmail.includes('@') ? loggedEmail : '';
  const autoPhone = !loggedEmail.includes('@') ? loggedEmail : '';
  
  const [formData, setFormData] = useState({
    property_address: prefill.property_address || '',
    region: prefill.region || '',
    property_city: '',
    property_state: 'CA',
    property_zip: '',
    latitude: null,
    longitude: null,
    package_type: 'standard',
    bedrooms: prefill.bedrooms || 2,
    bathrooms: prefill.bathrooms || 1,
    square_footage: prefill.square_footage || 550,
    upgrades: [],
    custom_upgrades: [],
    customer_name: prefill.customer_name || '',
    customer_email: autoEmail,
    customer_phone: autoPhone
  });

  // Google Places Autocomplete
  useGooglePlacesAutocomplete(addressInputRef, {
    componentRestrictions: { country: 'us' },
    onPlaceSelect: (addressData) => {
      setAddressConfirmed(true);
      setFormData(prev => ({
        ...prev,
        property_address: addressData.formatted_address || addressData.street_address,
        property_city: addressData.city,
        property_state: addressData.state || 'CA',
        property_zip: addressData.zip,
        latitude: addressData.latitude,
        longitude: addressData.longitude
      }));
    }
  });

  // Generate satellite map URL when address changes
  useEffect(() => {
    if (formData.property_address && GOOGLE_MAPS_API_KEY) {
      const encodedAddress = encodeURIComponent(formData.property_address);
      const mapImageUrl = `https://maps.googleapis.com/maps/api/staticmap?center=${encodedAddress}&zoom=20&size=800x400&maptype=satellite&markers=color:red%7C${encodedAddress}&key=${GOOGLE_MAPS_API_KEY}`;
      setMapUrl(mapImageUrl);
    } else {
      setMapUrl(null);
    }
  }, [formData.property_address]);

  // Auto-adjust square footage when bedrooms/bathrooms change
  useEffect(() => {
    const range = getRoomSizeRange(formData.bedrooms, formData.bathrooms);
    setFormData(prev => ({
      ...prev,
      square_footage: range.mid
    }));
  }, [formData.bedrooms, formData.bathrooms]);

  // Regional pricing logic
  const TORRANCE_CENTER = { lat: 33.8358, lng: -118.3406 };
  const MILES_TO_KM = 1.60934;
  const TORRANCE_RADIUS_KM = 8 * MILES_TO_KM;

  const getDistanceKm = (lat1, lng1, lat2, lng2) => {
    const R = 6371;
    const dLat = (lat2 - lat1) * Math.PI / 180;
    const dLng = (lng2 - lng1) * Math.PI / 180;
    const a = Math.sin(dLat / 2) ** 2 + Math.cos(lat1 * Math.PI / 180) * Math.cos(lat2 * Math.PI / 180) * Math.sin(dLng / 2) ** 2;
    return R * 2 * Math.atan2(Math.sqrt(a), Math.sqrt(1 - a));
  };

  const isPremiumRegion = () => {
    const { latitude, longitude } = formData;
    if (!latitude || !longitude) return false;
    // NorCal: Bay Area and north (lat > 37.3)
    if (latitude > 37.3) return true;
    // San Diego area (lat ~32.5-33.1)
    if (latitude >= 32.5 && latitude <= 33.15 && longitude >= -117.5 && longitude <= -116.7) return true;
    // Within 8 miles of Torrance
    if (getDistanceKm(latitude, longitude, TORRANCE_CENTER.lat, TORRANCE_CENTER.lng) <= TORRANCE_RADIUS_KM) return true;
    return false;
  };

  const premium = isPremiumRegion();
  const [dbPrices, setDbPrices] = useState([]);
  const [allPriceItems, setAllPriceItems] = useState([]);

  // Detect region from address for price matching
  const NORCAL_KEYWORDS = ['san francisco','san jose','oakland','sacramento','san mateo','santa clara','alameda','contra costa','marin','sonoma','napa','solano','santa cruz','monterey','fresno','stockton','modesto','fremont','hayward','sunnyvale','palo alto','mountain view','berkeley','richmond','vallejo','concord','san rafael','redwood city','milpitas','cupertino','campbell','los gatos','saratoga','gilroy','morgan hill','pleasanton','livermore','dublin','walnut creek','danville','san ramon','tracy','manteca','turlock','merced','visalia','bakersfield','san luis obispo'];
  const detectPriceRegion = () => {
    // Prefer explicit region choice (from Free Estimate form) over address detection
    if (formData.region === '南加' || formData.region === '北加') return formData.region;
    const addr = (formData.property_address || '').toLowerCase();
    for (const kw of NORCAL_KEYWORDS) { if (addr.includes(kw)) return '北加'; }
    if (formData.latitude && formData.latitude > 37.3) return '北加';
    return '南加';
  };
  const priceRegion = detectPriceRegion();

  // Load prices from backend
  useEffect(() => {
    const loadPrices = async () => {
      try {
        const [pricingResponse, itemsResponse] = await Promise.all([
          axios.get(`${API}/admin/pricing`),
          axios.get(`${API}/admin/price-items`),
        ]);
        setDbPrices(pricingResponse.data.pricing || []);
        setAllPriceItems(itemsResponse.data.items || []);
      } catch {}
    };
    loadPrices();
  }, []);

  // Region-aware price lookup: never borrow a price configured for another region.
  const getPrice = (name, fallback) => {
    if (!allPriceItems.length) return fallback;
    const regionMatch = allPriceItems.find(p => p.name === name && p.region === priceRegion);
    if (regionMatch) return regionMatch.unit_price;
    const allMatch = allPriceItems.find(p => p.name === name && (!p.region || p.region === ''));
    if (allMatch) return allMatch.unit_price;
    return fallback;
  };

  const getPackagePrice = (packageType, fallback) => {
    const region = priceRegion === '北加' ? 'norcal' : 'socal';
    const match = dbPrices.find(p => p.package_type === packageType && p.region === region);
    return match?.base_price_sqft ?? fallback;
  };

  const defaultPricing = {
    standard: getPackagePrice('standard', priceRegion === '北加' ? 408 : 246),
    premium: getPackagePrice('premium', priceRegion === '北加' ? 428 : 256),
    luxury: getPackagePrice('luxury', priceRegion === '北加' ? 448 : 266),
    smart: getPackagePrice('smart', priceRegion === '北加' ? 538 : 346),
  };
  const pricing = defaultPricing;

  // Bilingual package name lookup — reads name_zh from price_items first, falls
  // back to English on empty/missing. Admin can override Chinese labels via
  // the Price Items admin tab without redeploy.
  // Cleans common prefixes/suffixes so admin can fill either "ADU标准V1.0" or
  // simply "标准" — both render as "标准" on the package card.
  const cleanPkgLabel = (s) => (s || '').replace(/^ADU\s*/i, '').replace(/\s*V\d+\.\d+\s*$/i, '').trim();
  const pkgName = (engName, fallbackEn) => {
    const item = allPriceItems.find(p => p.name === engName);
    if (isZh) return cleanPkgLabel(item?.name_zh) || fallbackEn;
    return fallbackEn;
  };

  const packages = [
    { value: 'standard', version: 'V1.0', name: pkgName('ADU Standard V1.0', 'STANDARD'), price: pricing.standard, ceilingHeight: isZh ? '8 英尺' : '8ft' },
    { value: 'premium', version: 'V2.0', name: pkgName('ADU Premium V2.0', 'PREMIUM'), price: pricing.premium, ceilingHeight: isZh ? '9 英尺' : '9ft' },
    { value: 'luxury', version: 'V3.0', name: pkgName('ADU Luxury V3.0', 'LUXURY'), price: pricing.luxury, ceilingHeight: isZh ? '9 英尺 + 拱形' : '9ft + Vaulted' },
    { value: 'smart', version: 'V4.0', name: pkgName('ADU Smart Luxury V4.0', 'SMART LUXURY'), price: pricing.smart, ceilingHeight: isZh ? '9 英尺 + 拱形' : '9ft + Vaulted', hasSmartFeatures: true }
  ];

  const upgrades = [
    { id: 'electrical', category: 'Electrical', icon: Zap, name: '200Amp Panel Upgrade', description: 'Upgrade from 100Amp to 200Amp electrical panel', price: getPrice('200A Panel Upgrade', 2500) },
    { id: 'roofing', category: 'Roofing', icon: HomeIcon, name: 'Tile Roofing Upgrade', description: 'Upgrade from shingle to tile roofing', price: getPrice('Tile Roofing', 3500) },
    { id: 'interior_door', category: 'Interior Door', icon: Package, name: 'Luxury Interior Door', description: 'Upgrade to 82" luxury interior doors', price: getPrice('Luxury Interior Door', 1200) },
    { id: 'smart_home', category: 'Smart Home', icon: Sparkles, name: 'Smart Home Package', description: 'Smart locks, switches, outlets and camera system', price: getPrice('Smart Home Package', 5000) },
    { id: 'fotile', category: 'Kitchen', icon: ChefHat, name: 'FOTILE Range Hood', description: 'Upgrade to FOTILE brand range hood', price: getPrice('FOTILE Range Hood', 1800) },
    { id: 'smart_toilet', category: 'Bathroom', icon: ShowerHead, name: 'Smart Toilet Upgrade', description: 'Upgrade to smart toilet (x2)', price: getPrice('Smart Toilet x2', 2000) },
    { id: 'central_ac', category: 'HVAC', icon: Wind, name: 'Central AC Upgrade', description: 'Upgrade from Mini Split to Central AC', price: getPrice('Central AC', 4500) },
    { id: 'flooring', category: 'Flooring', icon: Footprints, name: 'Premium Vinyl Flooring', description: 'Upgrade to 10mm thick vinyl flooring', price: getPrice('Premium Vinyl Flooring', 1500) }
  ];

  const selectedPackage = packages.find(p => p.value === formData.package_type);
  const selectedPackageData = packageData.find(p => p.value === formData.package_type);
  
  const calculateQuote = () => {
    if (!selectedPackage) return null;
    const sqft = formData.square_footage;
    const pkgPrice = selectedPackage.price;
    const basePrice = sqft * pkgPrice;
    let surchargePerSqft = 0;
    if (sqft < 800) {
      surchargePerSqft = Math.ceil((800 - sqft) / 100) * 20;
    }
    const smallUnitSurcharge = sqft * surchargePerSqft;
    const upgradesTotal = formData.upgrades.reduce((sum, upgradeId) => {
      const upgrade = upgrades.find(u => u.id === upgradeId);
      return sum + (upgrade?.price || 0);
    }, 0);
    const customUpgradesTotal = formData.custom_upgrades.reduce((sum, item) => sum + item.price, 0);
    
    return {
      basePrice,
      smallUnitSurcharge,
      effectiveSqft: sqft,
      upgradesTotal,
      customUpgradesTotal,
      total: basePrice + smallUnitSurcharge + upgradesTotal + customUpgradesTotal
    };
  };

  const quote = calculateQuote();

  const toggleUpgrade = (upgradeId) => {
    setFormData(prev => ({
      ...prev,
      upgrades: prev.upgrades.includes(upgradeId)
        ? prev.upgrades.filter(id => id !== upgradeId)
        : [...prev.upgrades, upgradeId]
    }));
  };

  const addCustomUpgrade = () => {
    if (!customUpgradeName.trim()) {
      toast.error('Please enter an item name');
      return;
    }
    const price = parseFloat(customUpgradePrice) || 0;
    if (price < 0) {
      toast.error('Price cannot be negative');
      return;
    }
    
    setFormData(prev => ({
      ...prev,
      custom_upgrades: [...prev.custom_upgrades, { name: customUpgradeName.trim(), price }]
    }));
    setCustomUpgradeName('');
    setCustomUpgradePrice('');
    toast.success('Custom item added');
  };

  const removeCustomUpgrade = (index) => {
    setFormData(prev => ({
      ...prev,
      custom_upgrades: prev.custom_upgrades.filter((_, i) => i !== index)
    }));
  };

  const handleGenerateQuote = async () => {
    if (!formData.property_address) {
      toast.error('Please enter a property address');
      return;
    }
    
    setLoading(true);
    try {
      const response = await axios.post(`${API}/quotations/guest`, {
        customer_name: formData.customer_name || 'Quick Quote',
        customer_email: formData.customer_email || 'quote@aligcus.com',
        customer_phone: formData.customer_phone || '',
        property_address: formData.property_address,
        property_city: formData.property_city,
        property_state: formData.property_state,
        property_zip: formData.property_zip,
        square_footage: formData.square_footage,
        package_type: formData.package_type,
        bedrooms: formData.bedrooms,
        bathrooms: formData.bathrooms,
        price_region: priceRegion,
        additional_options: formData.upgrades,
        custom_upgrades: formData.custom_upgrades,
        notes: ''
      });
      
      setGeneratedQuote(response.data);
      setShowQuoteResult(true);
      toast.success('Quote generated successfully!');
    } catch (error) {
      console.error('Error:', error);
      const detail = error?.response?.data?.detail;
      if (Array.isArray(detail)) {
        toast.error(detail.map(d => d.msg).join('; '));
      } else {
        toast.error(typeof detail === 'string' ? detail : 'Failed to generate quote');
      }
    } finally {
      setLoading(false);
    }
  };

  const handlePrint = () => {
    window.print();
  };

  const handleExportPdf = async () => {
    if (!printRef.current) return;
    
    setExportingPdf(true);
    try {
      // 1) Pre-load logo as base64 for PDF (avoid CORS)
      let logoBase64 = null;
      try {
        const logoResp = await fetch('/api/uploads/8bb5e435c484.png');
        const logoBlob = await logoResp.blob();
        logoBase64 = await new Promise(r => { const rd = new FileReader(); rd.onloadend = () => r(rd.result); rd.readAsDataURL(logoBlob); });
      } catch {}
      const logoImg = printRef.current.querySelector('img[alt="ALIGCUS"]');
      const origSrc = logoImg?.src;
      if (logoImg && logoBase64) logoImg.src = logoBase64;

      // 2) Locate all PDF sections — each one becomes its own page block
      const sections = Array.from(printRef.current.querySelectorAll('[data-pdf-section]'));
      if (sections.length === 0) { setExportingPdf(false); return; }

      const pdf = new jsPDF('p', 'mm', 'a4');
      const pdfWidth = pdf.internal.pageSize.getWidth();
      const pdfHeight = pdf.internal.pageSize.getHeight();
      const margin = 10;            // mm — uniform white margin (cleaner look)
      const usableWidth = pdfWidth - margin * 2;
      const usableHeight = pdfHeight - margin * 2;
      let isFirstPage = true;

      // 3) Render each section. One section = one page (or multiple if it's tall).
      for (const section of sections) {
        // Capture this section at 2x scale for crisp output
        const canvas = await html2canvas(section, {
          scale: 2,
          useCORS: true,
          logging: false,
          backgroundColor: '#ffffff',
          allowTaint: true,
        });

        const pxPerMm = canvas.width / usableWidth;
        const sectionHeightMm = canvas.height / pxPerMm;

        if (sectionHeightMm <= usableHeight) {
          // — Fits in one page: render on its own page, no scaling down.
          if (!isFirstPage) pdf.addPage();
          isFirstPage = false;
          const img = canvas.toDataURL('image/jpeg', 0.95);
          pdf.addImage(img, 'JPEG', margin, margin, usableWidth, sectionHeightMm);
        } else {
          // — Section taller than a page: slice it inside the section only,
          //   so this category's data still stays together (no mix with next section).
          const sliceHeightPx = usableHeight * pxPerMm;
          let srcY = 0;
          while (srcY < canvas.height) {
            if (!isFirstPage) pdf.addPage();
            isFirstPage = false;

            const thisSlice = Math.min(sliceHeightPx, canvas.height - srcY);
            const tmp = document.createElement('canvas');
            tmp.width = canvas.width;
            tmp.height = thisSlice;
            const ctx = tmp.getContext('2d');
            ctx.fillStyle = '#ffffff';
            ctx.fillRect(0, 0, tmp.width, tmp.height);
            ctx.drawImage(canvas, 0, srcY, canvas.width, thisSlice, 0, 0, canvas.width, thisSlice);

            const img = tmp.toDataURL('image/jpeg', 0.95);
            const scaledH = thisSlice / pxPerMm;
            pdf.addImage(img, 'JPEG', margin, margin, usableWidth, scaledH);
            srcY += thisSlice;
          }
        }
      }

      // 4) Add page numbers in footer (subtle, bottom-right)
      const totalPages = pdf.internal.getNumberOfPages();
      for (let i = 1; i <= totalPages; i++) {
        pdf.setPage(i);
        pdf.setFontSize(8);
        pdf.setTextColor(150);
        pdf.text(`${i} / ${totalPages}`, pdfWidth - margin, pdfHeight - 4, { align: 'right' });
      }

      // Restore original src
      if (logoImg && origSrc) logoImg.src = origSrc;
      
      const addressSlug = formData.property_address.split(',')[0].replace(/\s+/g, '_').substring(0, 30);
      pdf.save(`ALIGCUS_Quote_${addressSlug}.pdf`);
      
      toast.success('PDF exported successfully!');
    } catch (error) {
      console.error('PDF export error:', error);
      toast.error('Failed to export PDF');
    } finally {
      setExportingPdf(false);
    }
  };

  const handleSendEmail = async () => {
    if (!emailAddress || !emailAddress.includes('@')) {
      toast.error('Please enter a valid email address');
      return;
    }
    
    setSendingEmail(true);
    try {
      const response = await axios.post(`${API}/email/send-quote`, {
        recipient_email: emailAddress,
        recipient_name: emailName || 'Valued Customer',
        property_address: formData.property_address,
        package_name: selectedPackage?.name,
        square_footage: formData.square_footage,
        bedrooms: formData.bedrooms,
        bathrooms: formData.bathrooms,
        total_price: quote?.total || 0
      });
      
      if (response.data.status === 'demo') {
        toast.success('Demo mode: Email functionality ready (configure API key to enable)');
      } else {
        toast.success('Quote sent to your email!');
      }
      setShowEmailModal(false);
      setEmailAddress('');
      setEmailName('');
    } catch (error) {
      console.error('Email error:', error);
      toast.error('Failed to send email');
    } finally {
      setSendingEmail(false);
    }
  };

  const getRecommendedSize = () => {
    const range = getRoomSizeRange(formData.bedrooms, formData.bathrooms);
    return `${range.min}-${range.max} sq.ft`;
  };

  const resetQuote = () => {
    setShowQuoteResult(false);
    setGeneratedQuote(null);
  };

  // Icon mapping for specification items
  const getItemIcon = (itemName) => {
    const name = itemName.toLowerCase();
    if (name.includes('foundation')) return Building2;
    if (name.includes('framing')) return Layers;
    if (name.includes('ceiling')) return Square;
    if (name.includes('roof')) return HomeIcon;
    if (name.includes('wall') && name.includes('exterior')) return Building2;
    if (name.includes('wall') && name.includes('insulation')) return Thermometer;
    if (name.includes('insulation')) return Thermometer;
    if (name.includes('panel') || name.includes('electrical')) return Zap;
    if (name.includes('outlet')) return Plug;
    if (name.includes('light')) return Lightbulb;
    if (name.includes('smoke') || name.includes('detector')) return Shield;
    if (name.includes('water supply') || name.includes('piping')) return Droplets;
    if (name.includes('drain')) return Pipette;
    if (name.includes('water heater')) return Waves;
    if (name.includes('paint') || name.includes('drywall')) return PaintBucket;
    if (name.includes('floor')) return Footprints;
    if (name.includes('door') && name.includes('interior')) return DoorOpen;
    if (name.includes('cabinet')) return Package;
    if (name.includes('counter')) return UtensilsCrossed;
    if (name.includes('sink')) return Droplets;
    if (name.includes('appliance') || name.includes('refrigerator')) return Refrigerator;
    if (name.includes('range') || name.includes('hood')) return ChefHat;
    if (name.includes('toilet')) return CircleDot;
    if (name.includes('shower') || name.includes('tub')) return ShowerHead;
    if (name.includes('vanity')) return Sofa;
    if (name.includes('fixture')) return Lamp;
    if (name.includes('heating') || name.includes('cooling') || name.includes('hvac')) return Wind;
    if (name.includes('ventilation') || name.includes('exhaust')) return Fan;
    if (name.includes('window')) return Square;
    if (name.includes('entry') || name.includes('door')) return DoorOpen;
    if (name.includes('gutter')) return Droplets;
    if (name.includes('smart') || name.includes('wifi')) return Wifi;
    if (name.includes('lock')) return KeyRound;
    return Check;
  };

  // Render detailed items list - clean, minimal design with icons
  const renderDetailedItemsList = () => {
    if (!selectedPackageData?.detailedItems) return null;
    
    const allCategories = [
      { key: 'structure', label: t('spec.structure'), icon: Building2 },
      { key: 'insulation', label: t('spec.insulation'), icon: Thermometer },
      { key: 'electrical', label: t('spec.electrical'), icon: Zap },
      { key: 'plumbing', label: t('spec.plumbing'), icon: Droplets },
      { key: 'interior', label: t('spec.interior'), icon: PaintBucket },
      { key: 'kitchen', label: t('spec.kitchen'), icon: ChefHat },
      { key: 'bathroom', label: t('spec.bathroom'), icon: ShowerHead },
      { key: 'hvac', label: t('spec.hvac'), icon: Wind },
      { key: 'exterior', label: t('spec.exterior'), icon: HomeIcon },
      { key: 'smartHome', label: t('spec.smartHome'), icon: Wifi }
    ];

    return (
      <div className="px-8 py-8 border-t border-gray-100">
        <h2 className="text-lg font-bold text-gray-900 mb-6 flex items-center gap-2">
          <Package className="w-5 h-5 text-red-600" />
          {selectedPackage?.name} {t('quoteResult.specs')}
        </h2>

        <div className="grid md:grid-cols-2 gap-6">
          {allCategories.map(({ key, label, icon: CategoryIcon }) => {
            const items = selectedPackageData.detailedItems[key];
            if (!items || items.length === 0) return null;
            
            return (
              <div key={key} className="print:break-inside-avoid">
                <div className="flex items-center gap-2 mb-3 pb-2 border-b border-gray-200">
                  <CategoryIcon className="w-4 h-4 text-red-600" />
                  <h3 className="text-sm font-bold text-gray-800 uppercase tracking-wide">{label}</h3>
                </div>
                
                <div className="space-y-2">
                  {items.map((item, index) => {
                    const ItemIcon = getItemIcon(item.item);
                    return (
                      <div key={index} className="flex items-start gap-3 py-2">
                        <ItemIcon className="w-4 h-4 text-gray-400 mt-0.5 flex-shrink-0" />
                        <div className="flex-1 min-w-0">
                          <span className="text-sm font-medium text-gray-900">{item.item}</span>
                          <span className="text-sm text-gray-500 ml-1">— {item.spec}</span>
                        </div>
                      </div>
                    );
                  })}
                </div>
              </div>
            );
          })}
        </div>
      </div>
    );
  };

  return (
    <div className="min-h-screen bg-white flex flex-col">
      <main className="flex-grow">
        {!showQuoteResult ? (
          /* Quote Form */
          <div className="max-w-7xl mx-auto px-6 py-8">
            <div className="grid lg:grid-cols-3 gap-8">
              <div className="lg:col-span-2 space-y-8">
                <div>
                  <h1 className="text-4xl font-black text-gray-900 italic mb-2">{t('quote.quickQuote')}</h1>
                  <p className="text-gray-600">{t('quote.getInstantEstimate')}</p>
                </div>

                {/* 0. Project Region — drives pricing */}
                <section data-testid="quote-region">
                  <h2 className="text-sm font-semibold text-gray-500 tracking-wider mb-4">{isZh ? '项目区域' : 'PROJECT REGION'} <span className="text-red-600">*</span></h2>
                  <div className="grid grid-cols-2 gap-4">
                    {[{ val: '南加', label: isZh ? '南加州' : 'Southern California' }, { val: '北加', label: isZh ? '北加州' : 'Northern California' }].map(r => {
                      const active = formData.region === r.val;
                      return (
                        <button key={r.val} type="button" onClick={() => setFormData({...formData, region: r.val})}
                          className={`flex items-center justify-center gap-2 px-4 py-3 rounded-lg border-2 font-medium transition-all ${active ? 'border-red-600 bg-red-50 text-red-600' : 'border-gray-200 text-gray-700 hover:border-red-300'}`}
                          data-testid={`quote-region-${r.val === '南加' ? 'socal' : 'norcal'}`}>
                          <span className={`w-4 h-4 rounded-full border-2 flex items-center justify-center ${active ? 'border-red-600' : 'border-gray-300'}`}>
                            {active && <span className="w-2 h-2 rounded-full bg-red-600" />}
                          </span>
                          {r.label}
                        </button>
                      );
                    })}
                  </div>
                  {!formData.region && (
                    <p className="text-xs text-red-600 mt-2" data-testid="region-required-hint">{isZh ? '* 请选择项目区域以计算价格' : '* Select a region to calculate pricing'}</p>
                  )}
                </section>

                {/* 1. Project Address */}
                <section>
                  <h2 className="text-sm font-semibold text-gray-500 tracking-wider mb-4">{t('quote.projectAddress')} <span className="text-red-600">*</span></h2>
                  <input
                    ref={addressInputRef}
                    type="text"
                    value={formData.property_address}
                    onChange={(e) => { setAddressConfirmed(false); setFormData({...formData, property_address: e.target.value}); }}
                    placeholder={t('quote.startTyping')}
                    className="w-full px-4 py-4 border-b-2 border-gray-200 focus:border-red-600 outline-none text-lg transition-colors"
                    data-testid="address-input"
                    required
                  />
                  {!formData.property_address ? (
                    <p className="text-xs text-red-600 mt-2" data-testid="address-required-hint">{isZh ? '* 此项为必填' : '* This field is required'}</p>
                  ) : !addressConfirmed && (
                    <p className="text-xs text-amber-600 mt-2" data-testid="address-select-hint">{isZh ? '请从下拉列表中选择一个具体地址' : 'Please select a specific address from the dropdown'}</p>
                  )}
                  
                  {mapUrl && addressConfirmed && (
                    <div className="mt-4">
                      <p className="text-sm text-gray-500 mb-2">{t('quote.satelliteView')}</p>
                      <InteractiveMap 
                        address={formData.property_address}
                        lat={formData.latitude}
                        lng={formData.longitude}
                        height="400px"
                        zoom={20}
                      />
                    </div>
                  )}
                </section>

                {/* 2. Select Package */}
                <section>
                  <h2 className="text-sm font-semibold text-gray-500 tracking-wider mb-4">{t('quote.selectPackage')}</h2>
                  <div className="grid grid-cols-2 gap-4">
                    {packages.map((pkg) => (
                      <Card
                        key={pkg.value}
                        className={`cursor-pointer transition-all duration-200 ${
                          formData.package_type === pkg.value
                            ? 'ring-2 ring-red-600 bg-red-50'
                            : 'hover:shadow-md border-gray-200'
                        }`}
                        onClick={() => setFormData({...formData, package_type: pkg.value})}
                        data-testid={`package-${pkg.value}`}
                      >
                        <CardContent className="p-4">
                          <div className="flex justify-between items-start">
                            <div>
                              <span className="text-xs text-gray-400">{pkg.version}</span>
                              <h3 className="font-bold text-gray-900">{pkg.name}</h3>
                              <p className="text-xs text-gray-500 mt-1">{pkg.ceilingHeight}</p>
                            </div>
                            {pkg.hasSmartFeatures && (
                              <Sparkles className="w-5 h-5 text-red-600" />
                            )}
                          </div>
                        </CardContent>
                      </Card>
                    ))}
                  </div>
                </section>

                {/* 3. Rooms */}
                <section>
                  <h2 className="text-sm font-semibold text-gray-500 tracking-wider mb-4">{t('quote.rooms')}</h2>
                  <div className="grid grid-cols-2 gap-4">
                    <Card className="border-gray-200">
                      <CardContent className="p-4">
                        <div className="flex items-center justify-between">
                          <div className="flex items-center gap-2">
                            <Bed className="w-5 h-5 text-gray-400" />
                            <span className="text-sm font-medium text-gray-600">{t('quote.bedrooms')}</span>
                          </div>
                          <div className="flex items-center gap-3">
                            <button
                              onClick={() => setFormData({...formData, bedrooms: Math.max(1, formData.bedrooms - 1)})}
                              className="w-8 h-8 rounded-full border border-gray-300 flex items-center justify-center hover:bg-gray-100"
                              data-testid="bedrooms-minus"
                            >
                              <Minus className="w-4 h-4" />
                            </button>
                            <span className="text-2xl font-bold w-8 text-center">{formData.bedrooms}</span>
                            <button
                              onClick={() => setFormData({...formData, bedrooms: Math.min(4, formData.bedrooms + 1)})}
                              className="w-8 h-8 rounded-full border border-gray-300 flex items-center justify-center hover:bg-gray-100"
                              data-testid="bedrooms-plus"
                            >
                              <Plus className="w-4 h-4" />
                            </button>
                          </div>
                        </div>
                      </CardContent>
                    </Card>

                    <Card className="border-gray-200">
                      <CardContent className="p-4">
                        <div className="flex items-center justify-between">
                          <div className="flex items-center gap-2">
                            <Bath className="w-5 h-5 text-gray-400" />
                            <span className="text-sm font-medium text-gray-600">{t('quote.bathrooms')}</span>
                          </div>
                          <div className="flex items-center gap-3">
                            <button
                              onClick={() => setFormData({...formData, bathrooms: Math.max(1, formData.bathrooms - 1)})}
                              className="w-8 h-8 rounded-full border border-gray-300 flex items-center justify-center hover:bg-gray-100"
                              data-testid="bathrooms-minus"
                            >
                              <Minus className="w-4 h-4" />
                            </button>
                            <span className="text-2xl font-bold w-8 text-center">{formData.bathrooms}</span>
                            <button
                              onClick={() => setFormData({...formData, bathrooms: Math.min(3, formData.bathrooms + 1)})}
                              className="w-8 h-8 rounded-full border border-gray-300 flex items-center justify-center hover:bg-gray-100"
                              data-testid="bathrooms-plus"
                            >
                              <Plus className="w-4 h-4" />
                            </button>
                          </div>
                        </div>
                      </CardContent>
                    </Card>
                  </div>
                </section>

                {/* 4. Square Footage */}
                <section>
                  <h2 className="text-sm font-semibold text-gray-500 tracking-wider mb-4">{t('quote.squareFootage')}</h2>
                  <Card className="border-gray-200">
                    <CardContent className="p-6">
                      <div className="flex justify-between items-center mb-4">
                        <span className="text-gray-600">{t('quote.size')}</span>
                        <div className="text-right">
                          <span className="text-3xl font-bold">{formData.square_footage}</span>
                          <span className="text-gray-500 ml-1">sq.ft</span>
                        </div>
                      </div>
                      
                      <div className="flex items-center gap-2 text-sm text-red-600 mb-4">
                        <Sparkles className="w-4 h-4" />
                        <span>{t('quote.recommendedFor', { beds: formData.bedrooms, baths: formData.bathrooms, range: getRecommendedSize() })}</span>
                      </div>
                      
                      <input
                        type="range"
                        min="500"
                        max="1200"
                        step="10"
                        value={formData.square_footage}
                        onChange={(e) => setFormData({...formData, square_footage: parseInt(e.target.value)})}
                        className="w-full h-2 bg-gray-200 rounded-lg appearance-none cursor-pointer accent-red-600"
                        data-testid="sqft-slider"
                      />
                      <div className="flex justify-between text-sm text-gray-500 mt-2">
                        <span>500 sq.ft</span>
                        <span>1200 sq.ft</span>
                      </div>
                      
                      {formData.square_footage < 800 && (
                        <div className="mt-4 p-3 bg-amber-50 border border-amber-200 rounded-lg">
                          <p className="text-sm text-amber-700">
                            {t('quote.surchargeNote')}
                          </p>
                        </div>
                      )}
                    </CardContent>
                  </Card>
                </section>

                {/* Property Value (for ROI) */}
                <section>
                  <h2 className="text-sm font-semibold text-gray-500 tracking-wider mb-4">
                    {isZh ? '5. 当前房屋估值' : '5. CURRENT PROPERTY VALUE'} <span className="font-normal text-gray-400">{t('quote.optional')}</span>
                  </h2>
                  <Card className="border-gray-200">
                    <CardContent className="p-4">
                      <div className="flex items-center gap-3">
                        <span className="text-2xl text-gray-400">$</span>
                        <input
                          type="number"
                          value={propertyValue}
                          onChange={(e) => setPropertyValue(e.target.value)}
                          placeholder={isZh ? '输入房屋当前市值（用于 ROI 分析）' : 'Enter current home value (for ROI analysis)'}
                          className="flex-1 px-3 py-2 border-b border-gray-200 focus:border-red-600 outline-none text-lg"
                          data-testid="property-value-input"
                        />
                      </div>
                      <p className="text-xs text-gray-400 mt-2">{isZh ? '此值用于生成投资回报分析，不影响报价' : 'Used for ROI analysis only, does not affect quote price'}</p>
                    </CardContent>
                  </Card>
                </section>

                {/* 6. Upgrades */}
                <section>
                  <h2 className="text-sm font-semibold text-gray-500 tracking-wider mb-4">{isZh ? '6. ' : '6. '}{t('quote.upgrades')} <span className="font-normal text-gray-400">{t('quote.optional')}</span></h2>
                  <div className="space-y-3">
                    {upgrades.map((upgrade) => {
                      const Icon = upgrade.icon;
                      const isSelected = formData.upgrades.includes(upgrade.id);
                      return (
                        <Card
                          key={upgrade.id}
                          className={`cursor-pointer transition-all duration-200 ${
                            isSelected ? 'ring-2 ring-red-600 bg-red-50' : 'hover:shadow-md border-gray-200'
                          }`}
                          onClick={() => toggleUpgrade(upgrade.id)}
                          data-testid={`upgrade-${upgrade.id}`}
                        >
                          <CardContent className="p-4">
                            <div className="flex items-center justify-between">
                              <div className="flex items-center gap-4">
                                <div className={`w-10 h-10 rounded-lg flex items-center justify-center ${isSelected ? 'bg-red-600 text-white' : 'bg-gray-100 text-gray-500'}`}>
                                  <Icon className="w-5 h-5" />
                                </div>
                                <div>
                                  <p className="text-xs text-gray-400 uppercase tracking-wider">{upgrade.category}</p>
                                  <h4 className="font-semibold text-gray-900">{upgrade.name}</h4>
                                  <p className="text-sm text-gray-500">{upgrade.description}</p>
                                </div>
                              </div>
                              <span className="text-red-600 font-bold">+${upgrade.price.toLocaleString()}</span>
                            </div>
                          </CardContent>
                        </Card>
                      );
                    })}
                    
                    {/* Custom Upgrades */}
                    <div className="mt-6 pt-6 border-t border-gray-200">
                      <h3 className="text-sm font-semibold text-gray-700 mb-3">{t('quote.addCustomItem')}</h3>
                      <div className="flex gap-3">
                        <input
                          type="text"
                          value={customUpgradeName}
                          onChange={(e) => setCustomUpgradeName(e.target.value)}
                          placeholder={t('quote.itemName')}
                          className="flex-1 px-4 py-3 border border-gray-300 rounded-lg focus:border-red-600 outline-none"
                          data-testid="custom-upgrade-name"
                        />
                        <input
                          type="number"
                          value={customUpgradePrice}
                          onChange={(e) => setCustomUpgradePrice(e.target.value)}
                          placeholder={t('quote.price')}
                          min="0"
                          className="w-32 px-4 py-3 border border-gray-300 rounded-lg focus:border-red-600 outline-none"
                          data-testid="custom-upgrade-price"
                        />
                        <Button onClick={addCustomUpgrade} className="bg-gray-900 hover:bg-gray-800 text-white px-6" data-testid="add-custom-upgrade-btn">
                          <Plus className="w-5 h-5" />
                        </Button>
                      </div>
                      
                      {formData.custom_upgrades.length > 0 && (
                        <div className="mt-4 space-y-2">
                          {formData.custom_upgrades.map((item, index) => (
                            <div key={index} className="flex items-center justify-between p-3 bg-gray-50 rounded-lg">
                              <span className="font-medium text-gray-900">{item.name}</span>
                              <div className="flex items-center gap-3">
                                <span className="text-red-600 font-bold">+${item.price.toLocaleString()}</span>
                                <button onClick={() => removeCustomUpgrade(index)} className="p-1 text-gray-400 hover:text-red-600">
                                  <Trash2 className="w-4 h-4" />
                                </button>
                              </div>
                            </div>
                          ))}
                        </div>
                      )}
                    </div>
                  </div>
                </section>

                <Button
                  onClick={handleGenerateQuote}
                  disabled={loading || !formData.property_address || !addressConfirmed}
                  className="w-full bg-red-600 hover:bg-red-700 text-white font-bold py-6 text-lg rounded-lg"
                  data-testid="generate-quote-btn"
                >
                  {loading ? t('quote.generating') : t('quote.generateQuote')}
                </Button>
              </div>

              {/* Right Column - Summary */}
              <div className="lg:col-span-1">
                <div className="sticky top-24">
                  <Card className="border-gray-200 shadow-lg">
                    <CardContent className="p-8 text-center">
                      {quote && formData.region ? (
                        <>
                          <div className="text-3xl sm:text-5xl font-black text-gray-900 mb-2">${quote.total.toLocaleString()}</div>
                          <p className="text-gray-500 mb-6">{t('quote.estimatedTotal')}</p>
                          
                          <div className="text-left space-y-3 border-t pt-4">
                            <div className="flex justify-between text-sm">
                              <span className="text-gray-600">{t('quote.base')} ({quote.effectiveSqft || formData.square_footage} sf)</span>
                              <span className="font-medium">${quote.basePrice.toLocaleString()}</span>
                            </div>
                            {quote.smallUnitSurcharge > 0 && (
                              <div className="flex justify-between text-sm text-amber-600">
                                <span>{t('quote.smallUnitSurcharge')}</span>
                                <span>+${quote.smallUnitSurcharge.toLocaleString()}</span>
                              </div>
                            )}
                            {quote.upgradesTotal > 0 && (
                              <div className="flex justify-between text-sm">
                                <span className="text-gray-600">{t('quote.upgradesLabel')}</span>
                                <span className="font-medium">+${quote.upgradesTotal.toLocaleString()}</span>
                              </div>
                            )}
                            {quote.customUpgradesTotal > 0 && (
                              <div className="flex justify-between text-sm">
                                <span className="text-gray-600">{t('quote.customItems')}</span>
                                <span className="font-medium">+${quote.customUpgradesTotal.toLocaleString()}</span>
                              </div>
                            )}
                          </div>
                        </>
                      ) : (
                        <>
                          <div className="w-20 h-20 mx-auto mb-4 rounded-full border-2 border-dashed border-gray-300 flex items-center justify-center">
                            <span className="text-3xl text-gray-300">$</span>
                          </div>
                          <p className="text-gray-500">{isZh ? '请先选择项目区域（南加州 / 北加州）以查看估价' : 'Select a project region (SoCal / NorCal) to see your estimate'}</p>
                        </>
                      )}
                    </CardContent>
                  </Card>
                </div>
              </div>
            </div>
          </div>
        ) : (
          /* Quote Result - Clean, Minimal Design */
          <div className="max-w-4xl mx-auto px-4 md:px-6 py-6">
            <div ref={printRef} className="bg-white">
              
              {/* ============ PDF SECTION 1: 项目摘要 ============ */}
              <div data-pdf-section="summary" className="pdf-section">
                {/* Simple Header - Logo on right */}
                <div className="flex items-center justify-between pb-4 mb-6 border-b-2 border-red-600">
                  <div className="text-sm text-gray-500">
                    <p className="font-semibold text-gray-900">{t('quoteResult.aduQuote')}</p>
                    <p>{new Date().toLocaleDateString('en-US', { year: 'numeric', month: 'short', day: 'numeric' })}</p>
                  </div>
                  <img src="/api/uploads/8bb5e435c484.png" alt="ALIGCUS" className="h-12" />
                </div>

                {/* Address */}
                <div className="mb-6">
                  <p className="text-xs text-gray-400 uppercase tracking-wider mb-1">{t('quoteResult.projectLocation')}</p>
                  <h1 className="text-2xl font-bold text-gray-900">{formData.property_address}</h1>
                </div>

                {/* Satellite Map */}
                {mapUrl && (
                  <div className="mb-6 rounded-lg overflow-hidden border border-gray-200">
                    <img 
                      src={mapUrl} 
                      alt="Satellite view" 
                      className="w-full h-48 object-cover"
                      data-testid="quote-satellite-map"
                    />
                  </div>
                )}

                {/* Quick Summary - Horizontal Cards */}
                <div className="grid grid-cols-4 gap-3 mb-6">
                  <div className="bg-red-600 text-white rounded-lg p-4 text-center">
                    <p className="text-xs opacity-80 mb-1">{t('quoteResult.package')}</p>
                    <p className="text-lg font-bold">{selectedPackage?.name}</p>
                  </div>
                  <div className="bg-red-600 text-white rounded-lg p-4 text-center">
                    <p className="text-xs opacity-80 mb-1">{t('quoteResult.sizeLabel')}</p>
                    <p className="text-lg font-bold">{formData.square_footage} <span className="text-sm font-normal">sf</span></p>
                  </div>
                  <div className="bg-red-600 text-white rounded-lg p-4 text-center">
                    <p className="text-xs opacity-80 mb-1">{t('quoteResult.layout')}</p>
                    <p className="text-lg font-bold">{formData.bedrooms}B/{formData.bathrooms}BA</p>
                  </div>
                  <div className="bg-red-600 text-white rounded-lg p-4 text-center">
                    <p className="text-xs opacity-80 mb-1">{t('quoteResult.ceiling')}</p>
                    <p className="text-lg font-bold">{selectedPackage?.ceilingHeight?.split(' ')[0]}</p>
                  </div>
                </div>

                {/* Price Summary - Clean Table */}
                <div className="mb-6 border border-gray-200 rounded-lg overflow-hidden">
                  <div className="bg-gray-50 px-4 py-3 border-b border-gray-200">
                    <h2 className="text-sm font-bold text-gray-700 uppercase tracking-wide">{t('quoteResult.priceSummary')}</h2>
                  </div>
                  <div className="divide-y divide-gray-100">
                    <div className="flex justify-between items-center px-4 py-3">
                      <span className="text-gray-600">{t('quote.base')} ({quote?.effectiveSqft || formData.square_footage} sf)</span>
                      <span className="font-semibold text-gray-900">${quote?.basePrice.toLocaleString()}</span>
                    </div>
                    
                    {quote?.smallUnitSurcharge > 0 && (
                      <div className="flex justify-between items-center px-4 py-3 bg-amber-50">
                        <span className="text-amber-700">{t('quote.smallUnitSurcharge')}</span>
                        <span className="font-semibold text-amber-700">+${quote?.smallUnitSurcharge.toLocaleString()}</span>
                      </div>
                    )}
                    
                    {formData.upgrades.map(upgradeId => {
                      const upgrade = upgrades.find(u => u.id === upgradeId);
                      return upgrade ? (
                        <div key={upgradeId} className="flex justify-between items-center px-4 py-3">
                          <span className="text-gray-600">{upgrade.name}</span>
                          <span className="font-semibold text-gray-900">+${upgrade.price.toLocaleString()}</span>
                        </div>
                      ) : null;
                    })}
                    
                    {formData.custom_upgrades.map((item, index) => (
                      <div key={index} className="flex justify-between items-center px-4 py-3">
                        <span className="text-gray-600">{item.name}</span>
                        <span className="font-semibold text-gray-900">+${item.price.toLocaleString()}</span>
                      </div>
                    ))}
                  </div>
                  
                  {/* Total */}
                  <div className="bg-gray-100 px-4 py-4 flex justify-between items-center">
                    <span className="font-bold text-gray-900">{t('quoteResult.totalEstimate')}</span>
                    <span className="text-3xl font-black text-gray-900">${quote?.total.toLocaleString()}</span>
                  </div>
                </div>

                {/* Notes - Compact */}
                <div className="mb-2 text-xs text-gray-500 bg-gray-50 rounded-lg p-4">
                  <p className="font-semibold text-gray-700 mb-2">{t('quoteResult.notes')}</p>
                  <ul className="space-y-1">
                    <li>&bull; {t('quoteResult.note1')}</li>
                    <li>&bull; {t('quoteResult.note2')}</li>
                    <li>&bull; {t('quoteResult.note3')}</li>
                  </ul>
                </div>
              </div>

              {/* ============ PDF SECTION 2: 投资分析 - 数据 ============ */}
              <div data-pdf-section="roi-main" className="pdf-section mt-6">
                <ROIAnalysis
                  squareFootage={formData.square_footage}
                  totalCost={quote?.total || 0}
                  bedrooms={formData.bedrooms}
                  bathrooms={formData.bathrooms}
                  isPremiumRegion={premium}
                  propertyValue={propertyValue ? parseFloat(propertyValue) : null}
                  renderMode="main"
                />
              </div>

              {/* ============ PDF SECTION 2B: 投资分析 - 大数据洞察 ============ */}
              <div data-pdf-section="roi-ai" className="pdf-section mt-6">
                <ROIAnalysis
                  squareFootage={formData.square_footage}
                  totalCost={quote?.total || 0}
                  bedrooms={formData.bedrooms}
                  bathrooms={formData.bathrooms}
                  isPremiumRegion={premium}
                  propertyValue={propertyValue ? parseFloat(propertyValue) : null}
                  renderMode="ai"
                />
              </div>

              {/* ============ PDF SECTION 3: 规格明细 ============ */}
              <div data-pdf-section="specs" className="pdf-section mt-6">
                {renderDetailedItemsList()}
              </div>
              
              {/* ============ PDF SECTION 4: 联系页脚 ============ */}
              <div data-pdf-section="footer" className="pdf-section mt-6 pt-4 pb-6 border-t border-gray-200 flex justify-between items-center text-sm">
                <div className="text-gray-500">
                  <p className="font-semibold text-gray-900">ALIGCUS ADU</p>
                  <p>www.aligcus.com</p>
                </div>
                <div className="text-right text-gray-600">
                  <p>SoCal: <span className="font-semibold">626-803-8000</span></p>
                  <p>NorCal: <span className="font-semibold">408-900-6688</span></p>
                  <p>San Diego: <span className="font-semibold">858-910-0000</span></p>
                </div>
              </div>
            </div>

            {/* Action Buttons - Simple */}
            <div className="mt-6 print:hidden flex flex-wrap gap-3 justify-center">
              <Button
                onClick={handleExportPdf}
                disabled={exportingPdf}
                className="bg-red-600 hover:bg-red-700 text-white px-6 py-2"
                data-testid="export-pdf-btn"
              >
                <Download className="w-4 h-4 mr-2" />
                {exportingPdf ? t('quoteResult.exporting') : t('quoteResult.downloadPdf')}
              </Button>
              <Button
                onClick={() => setShowEmailModal(true)}
                variant="outline"
                className="border-gray-300 text-gray-700 px-6 py-2"
                data-testid="email-quote-btn"
              >
                <Mail className="w-4 h-4 mr-2" />
                {t('quoteResult.email')}
              </Button>
              <Button
                onClick={handlePrint}
                variant="outline"
                className="border-gray-300 text-gray-700 px-6 py-2"
              >
                <Printer className="w-4 h-4 mr-2" />
                {t('quoteResult.print')}
              </Button>
              <Button
                onClick={resetQuote}
                variant="outline"
                className="border-gray-300 text-gray-700 px-6 py-2"
              >
                {t('quoteResult.newQuote')}
              </Button>
            </div>
            
            {/* Contact Links */}
            <div className="mt-4 print:hidden flex justify-center gap-4 text-sm">
              <a href="tel:626-803-8000" className="flex items-center gap-2 text-gray-600 hover:text-red-600">
                <Phone className="w-4 h-4" />SoCal: 626-803-8000
              </a>
              <a href="tel:408-900-6688" className="flex items-center gap-2 text-gray-600 hover:text-red-600">
                <Phone className="w-4 h-4" />NorCal: 408-900-6688
              </a>
              <a href="tel:858-910-0000" className="flex items-center gap-2 text-gray-600 hover:text-red-600">
                <Phone className="w-4 h-4" />San Diego: 858-910-0000
              </a>
            </div>
          </div>
        )}
      </main>

      {/* Email Modal */}
      {showEmailModal && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 print:hidden">
          <div className="bg-white rounded-xl p-6 w-full max-w-md mx-4 shadow-2xl">
            <div className="flex items-center justify-between mb-4">
              <h3 className="text-xl font-bold text-gray-900">{t('quoteResult.sendEmail')}</h3>
              <button onClick={() => setShowEmailModal(false)} className="text-gray-400 hover:text-gray-600">
                <X className="w-6 h-6" />
              </button>
            </div>
            
            <div className="space-y-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">{t('quoteResult.yourName')}</label>
                <input
                  type="text"
                  value={emailName}
                  onChange={(e) => setEmailName(e.target.value)}
                  placeholder={t('quoteResult.enterName')}
                  className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:border-red-600 outline-none"
                  data-testid="email-name-input"
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">{t('quoteResult.emailAddress')}</label>
                <input
                  type="email"
                  value={emailAddress}
                  onChange={(e) => setEmailAddress(e.target.value)}
                  placeholder={t('quoteResult.enterEmail')}
                  className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:border-red-600 outline-none"
                  data-testid="email-address-input"
                />
              </div>
              
              <PrivacyConsent agreed={emailPrivacyAgreed} setAgreed={setEmailPrivacyAgreed} />

              <Button
                onClick={handleSendEmail}
                disabled={sendingEmail || !emailPrivacyAgreed}
                className="w-full bg-red-600 hover:bg-red-700 text-white font-bold py-4"
                data-testid="send-email-btn"
              >
                <Mail className="w-5 h-5 mr-2" />
                {sendingEmail ? t('quoteResult.sending') : t('quoteResult.sendQuote')}
              </Button>
            </div>
          </div>
        </div>
      )}

      {!showQuoteResult && (
        <div className="print:hidden">
          <Footer />
        </div>
      )}

      <style>{`
        @media print {
          body { print-color-adjust: exact; -webkit-print-color-adjust: exact; }
          .print\\:hidden { display: none !important; }
          header { display: none !important; }
          nav[data-testid="main-navbar"] { display: none !important; }
          .page-break-before { page-break-before: always; }
          @page { margin: 10mm; }
        }
      `}</style>
    </div>
  );
};

export default QuoteCalculator;
